import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown, Checkbox, Input, Form, Icon } from "semantic-ui-react";
import Switch from "react-switch";
import {
  toggleOutbound,
  setWorkspaces,
  setParsedFields,
  setSelectedValues,
  setRequiredSetting,
  fetchOutboundWorkspaces,
} from "../../store/outboundVoiceSlice";
import { WEBHOOK_TIME_FILTER_FIELD } from "./webhookTimeFilterConstants";

/**
 * OutboundVoiceWebhook
 *
 * UI for configuring outbound voice webhook rules.
 *
 * INPUT PROPS:
 * - showOutbound (fallbackWebhook):
 *   If fallback webhook is enabled, outbound configuration is hidden.
 *
 * STATE (Redux):
 * - outboundVoice slice manages:
 *   - enabled toggle
 *   - available workspaces
 *   - selected workspaces
 *   - selected filter fields & values
 *   - required setting per workspace
 */
const OutboundVoiceWebhook = ({ showOutbound: fallbackWebhook, formErrors, onClearFormErrorKey, }) => {
  const dispatch = useDispatch();
  const [toggleLoading, setToggleLoading] = React.useState(false);
  const toggleTimerRef = React.useRef(null);

  // Read outbound voice state from Redux
  const outbound = useSelector((s) => s.outboundVoice);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (toggleTimerRef.current) {
        clearTimeout(toggleTimerRef.current);
      }
    };
  }, []);

  /**
   * Show outbound configuration only when:
   * - Outbound is enabled
   * - Fallback webhook is NOT enabled
   */
  const showOutboundSection =
    outbound.enabled === true &&
    (fallbackWebhook === 0 ||
      fallbackWebhook === "0" ||
      fallbackWebhook === false ||
      !fallbackWebhook);

  /**
   * Fetch outbound-enabled workspaces on first render.
   * Data is cached in Redux, so we avoid refetching
   * if workspaces already exist.
   */
  useEffect(() => {
      dispatch(fetchOutboundWorkspaces());
  }, [dispatch]);

  /**
   * Debounced toggle handler to prevent rapid API calls
   */
  const handleToggleOutbound = (value) => {
    // Prevent rapid toggling while loading
    if (toggleLoading) {
      console.warn('Outbound toggle already in progress, ignoring');
      return;
    }

    // Clear any pending timer
    if (toggleTimerRef.current) {
      clearTimeout(toggleTimerRef.current);
    }

    // Set loading state
    setToggleLoading(true);

    // Debounce the toggle action
    toggleTimerRef.current = setTimeout(() => {
      dispatch(toggleOutbound(value));
      setToggleLoading(false);
    }, 300); // 300ms debounce
  };

  /**
   * Helper to fetch parsed fields for a workspace
   *
   * OUTPUT:
   * - Array of parsed webhook fields for given workspace
   * - Returns empty array if workspace not found or fields invalid
   */
  const getParsedFields = (workspaceId) => {
    if (!workspaceId) {
      return [];
    }

    if (!outbound.workspaces || !Array.isArray(outbound.workspaces)) {
      console.warn('getParsedFields: workspaces array is invalid');
      return [];
    }

    const workspace = outbound.workspaces.find((w) => w && w.workspace_id === workspaceId);

    if (!workspace) {
      return [];
    }

    const fields = workspace.parsed_fields;

    if (!fields || !Array.isArray(fields)) {
      return [];
    }

    // Filter out invalid field entries
    return fields.filter(field =>
      field &&
      field.key &&
      typeof field.key === 'string' &&
      field.key.trim() !== ''
    );
  };

  return (
    <>
      {/* =====================================================
          Enable Outbound Voice Toggle
         ===================================================== */}
      <div className="webhook-section-card">
        <div className="webhook-section-header">
          <div className="webhook-section-title">
            <label className="webhook-main-label">Outbound Voice</label>

            {/* Toggle outbound voice feature */}
            <Switch
              onChange={handleToggleOutbound}
              checked={outbound.enabled}
              disabled={toggleLoading}
              onColor="#86d3ff"
              onHandleColor="#2693e6"
              handleDiameter={30}
              uncheckedIcon={false}
              checkedIcon={false}
              boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
              activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
              height={20}
              width={48}
              className="react-switch"
              id="material-switch"
            />
          </div>
        </div>

        {/* =====================================================
            Outbound Workspace Configuration Section
           ===================================================== */}
        {showOutboundSection && (
          <div className="webhook-card-content">
            <div className="webhook-filter-rule">
              <label className="webhook-filter-label">
                Add Outbound Workspaces
              </label>

              {/* Workspace multi-select dropdown */}
              <Dropdown
                placeholder="Searchable Select Workspaces"
                fluid
                multiple
                search
                selection
                icon="search"
                className="webhook-searchable-dropdown"
                options={
                  outbound.workspaces && Array.isArray(outbound.workspaces) && outbound.workspaces.length > 0
                    ? outbound.workspaces
                        .filter(w => w && w.workspace_id && w.workspace_name)
                        .map((w) => ({
                          key: w.workspace_id,
                          text: w.workspace_name,
                          value: w.workspace_id,
                        }))
                    : []
                }
                value={outbound.selectedWorkspaces || []}
                onChange={(e, d) => dispatch(setWorkspaces(d.value))}
              />

            {/* =====================================================
                Per-Workspace Rule Configuration
               ===================================================== */}
            {(outbound.selectedWorkspaces || []).map((wsId) => {
              // Skip invalid workspace IDs
              if (!wsId) {
                return null;
              }

              const fields = getParsedFields(wsId);
              const selectedFieldKeys = outbound.parsedFields?.[wsId] || [];
              const rowSelectedValues = outbound.selectedValues?.[wsId] || {};
              const workspace = outbound.workspaces?.find(
                (w) => w && w.workspace_id === wsId
              );
              const workspaceName = workspace?.workspace_name || `Workspace ${wsId}`;

              return (
                <div
                  key={`workspace-${wsId}`}
                  className="webhook-workspace-config"
                >
                  <>
                      <label className="webhook-filter-label">
                        Add webhook filtering rules for{" "}
                        <b>{workspaceName}</b>
                      </label>

                      {/* Select fields to apply filters on (Time first, then parse fields) */}
                      <Dropdown
                        placeholder="Select filtering fields"
                        fluid
                        multiple
                        selection
                        options={
                          (() => {
                            const timeOption = {
                              key: `${wsId}-time`,
                              text: "Time-based",
                              value: WEBHOOK_TIME_FILTER_FIELD,
                            };
                            const rest =
                              fields && Array.isArray(fields) && fields.length > 0
                                ? fields
                                    .filter(
                                      (f) =>
                                        f &&
                                        f.key &&
                                        typeof f.key === "string" &&
                                        f.key.trim() !== ""
                                    )
                                    .map((f, idx) => ({
                                      key: `${wsId}-field-${idx}`,
                                      text: f.key,
                                      value: f.key,
                                    }))
                                : [];
                            if (rest.length === 0) {
                              return [timeOption];
                            }
                            return [timeOption, ...rest];
                          })()
                        }
                        value={selectedFieldKeys}
                        onChange={(e, d) => {
                          const v = (d.value || []).filter(
                            (val) =>
                              typeof val === "string" && !val.startsWith("header")
                          );
                          dispatch(
                            setParsedFields({
                              workspaceId: wsId,
                              fields: v,
                            })
                          );
                        }}
                      />

                      {/* =====================================================
                          Field Value Selection
                         ===================================================== */}
                      {selectedFieldKeys.map((field) => {
                        if (!field || typeof field !== "string" || field.trim() === "") {
                          return null;
                        }

                        if (field === WEBHOOK_TIME_FILTER_FIELD) {
                          const raw = (rowSelectedValues[field] || [])[0] ?? "";
                          const timeInputId = `webhook-outbound-voice-time-${wsId}`;
                          const timeErr = Boolean(formErrors?.[`outbound_time_${wsId}`]);
                          return (
                            <div
                              key={`${wsId}-${field}`}
                              className="webhook-filter-rule webhook-time-threshold-field webhook-time-based-filter-block"
                            >
                              <div className="webhook-time-based-filter-header">
                                <div className="webhook-time-based-filter-icon-wrap" aria-hidden="true">
                                  <Icon name="clock outline" />
                                </div>
                                <div className="webhook-time-based-filter-header-text">
                                  <div className="webhook-time-based-filter-title">Time-based filter</div>
                                  <p className="webhook-time-based-filter-desc">
                                    Webhook fires only when a call exceeds a set duration threshold.
                                  </p>
                                </div>
                              </div>
                              <Form.Field className="webhook-time-based-filter-form-field">
                                <div className="webhook-time-based-filter-card">
                                  <div className="webhook-time-based-filter-card-heading">Rule configuration</div>
                                  <div className="webhook-time-rule-inline">
                                    <span className="webhook-time-rule-lead">
                                      Webhook will trigger if the call exceeds
                                    </span>
                                    <span
                                      className={
                                        "webhook-time-rule-input-cluster" +
                                        (timeErr ? " webhook-time-rule-input-cluster--error" : "")
                                      }
                                    >
                                      <Input
                                        id={timeInputId}
                                        className="webhook-time-rule-numeric-input"
                                        type="number"
                                        input={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                        style={{ padding: "0px 6px 0px 6px" }}
                                        value={raw}
                                        min={0}
                                        name="webhook_outbound_voice_time_seconds"
                                        onChange={(e, d) => {
                                          dispatch(
                                            setSelectedValues({
                                              workspaceId: wsId,
                                              field: WEBHOOK_TIME_FILTER_FIELD,
                                              values:
                                                d.value === "" || d.value === undefined
                                                  ? []
                                                  : [String(d.value).trim()],
                                            })
                                          );
                                          const errKey = `outbound_time_${wsId}`;
                                          if (formErrors?.[errKey] && onClearFormErrorKey) {
                                            onClearFormErrorKey(errKey);
                                          }
                                        }}
                                      />
                                      <span className="webhook-time-rule-unit-s">s</span>
                                    </span>
                                    <span className="webhook-time-rule-trail">seconds</span>
                                  </div>
                                </div>
                              </Form.Field>
                              {formErrors?.[`outbound_time_${wsId}`] && (
                                <div className="webhook-error-message" role="alert">
                                  {formErrors[`outbound_time_${wsId}`]}
                                </div>
                              )}
                            </div>
                          );
                        }

                        const f = fields.find((x) => x && x.key === field);

                        // Skip if field not found or has no values
                        if (!f) {
                          console.warn(`Field "${field}" not found in workspace ${wsId}`);
                          return null;
                        }

                        if (!f.values || !Array.isArray(f.values) || f.values.length === 0) {
                          return null;
                        }

                        // Filter out invalid values
                        const validValues = f.values.filter(v =>
                          v !== null && v !== undefined && String(v).trim() !== ''
                        );

                        if (validValues.length === 0) {
                          return null;
                        }

                        return (
                          <div key={`${wsId}-${field}`} className="webhook-filter-rule">
                            <label className="webhook-filter-label">
                              Webhook will trigger if{" "}
                              <b>{field}</b> option equals
                            </label>

                            <Dropdown
                              fluid
                              multiple
                              selection
                              placeholder={`Choose ${field} value`}
                              options={validValues.map((v, idx) => ({
                                key: `${wsId}-${field}-${idx}`,
                                text: String(v),
                                value: String(v),
                              }))}
                              value={rowSelectedValues[field] || []}
                              onChange={(e, d) =>
                                dispatch(
                                  setSelectedValues({
                                    workspaceId: wsId,
                                    field,
                                    values: d.value,
                                  })
                                )
                              }
                            />
                          </div>
                        );
                      })}

                      {/* =====================================================
                          Required Fields Rule
                         ===================================================== */}
                      <div className="webhook-checkbox-container">
                        <Checkbox
                          checked={
                            outbound.requiredSettings[wsId] || false
                          }
                          onChange={(e, data) =>
                            dispatch(
                              setRequiredSetting({
                                workspaceId: wsId,
                                value: data.checked,
                              })
                            )
                          }
                        />
                        <label
                          className="webhook-checkbox-label"
                          onClick={() =>
                            dispatch(
                              setRequiredSetting({
                                workspaceId: wsId,
                                value: !outbound.requiredSettings[wsId],
                              })
                            )
                          }
                        >
                          Use the "if all required fields are gathered" rule for {workspaceName} to determine when to pass a payload
                        </label>
                      </div>
                  </>
                </div>
              );
            })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default OutboundVoiceWebhook;
